<template id="admin-razorpay">
  <section>
    <div class="d-flex justify-content-between align-items-center">
      <h4>Razorpay Transactions</h4>
      <b-button variant="info" size="sm" @click.prevent="load" :disabled="loading">
        <font-awesome-icon :icon="['far','sync']" :spin="loading"></font-awesome-icon>
        Refresh
      </b-button>
    </div>

    <div class="d-flex flex-column flex-md-row align-items-md-end">
      <b-form-select v-model="range" class="mr-0 mr-md-3 mt-3" @change="onRangeChange">
        <option value="TODAY">Today</option>
        <option value="YESTERDAY">Yesterday</option>
        <option value="SEVEN">Last 7 Days</option>
        <option value="THIRTY">Last 30 Days</option>
        <option value="CUSTOM">Custom</option>
      </b-form-select>
      <b-form-input v-if="range === 'CUSTOM'" type="date" v-model="from" class="mr-0 mr-md-3 mt-3" @change="load"/>
      <b-form-input v-if="range === 'CUSTOM'" type="date" v-model="to" class="mr-0 mr-md-3 mt-3" @change="load"/>
      <b-input-group class="mt-3">
        <b-form-input v-model="filter" placeholder="Search payment id, order, email, phone, customer"
                      @keyup.enter="searchById"/>
        <b-input-group-append v-if="isPaymentId && !filtered.length">
          <b-button variant="outline-info" @click="searchById">Lookup on Razorpay</b-button>
        </b-input-group-append>
      </b-input-group>
    </div>

    <div class="d-flex flex-wrap mt-3">
      <b-card class="mr-3 mb-2 bg-transparent" body-class="py-2 px-3">
        <small class="text-muted d-block">Collected (captured)</small>
        <strong class="text-success"><rupee>{{ money(capturedAmt) }}</rupee></strong>
      </b-card>
      <b-card class="mr-3 mb-2 bg-transparent" body-class="py-2 px-3">
        <small class="text-muted d-block">Success rate</small>
        <strong>{{ successRate }}</strong>
      </b-card>
      <b-card class="mr-3 mb-2 bg-transparent" body-class="py-2 px-3">
        <small class="text-muted d-block">Failed</small>
        <strong class="text-danger">{{ counts.failed || 0 }}</strong>
      </b-card>
      <b-card class="mr-3 mb-2 bg-transparent" body-class="py-2 px-3">
        <small class="text-muted d-block">Refunded</small>
        <strong class="text-info"><rupee>{{ money(refundedAmt) }}</rupee></strong>
      </b-card>
    </div>

    <b-nav pills class="mt-2 mb-3">
      <b-nav-item v-for="s of statuses" :key="s.key" :active="status === s.key" @click="status = s.key">
        {{ s.label }}
        <b-badge variant="light" class="ml-1">{{ s.key === 'all' ? payments.length : (counts[s.key] || 0) }}</b-badge>
      </b-nav-item>
    </b-nav>

    <b-alert variant="danger" :show="!!error">{{ error }}</b-alert>

    <b-table :items="filtered" :fields="fields" :busy="loading" hover small responsive stacked="md"
             show-empty empty-text="No transactions" tbody-tr-class="cursor-pointer"
             @row-clicked="p => selected = p">
      <template #table-busy>
        <div class="text-center my-3">
          <b-spinner small class="mr-2"></b-spinner>
          Loading from Razorpay…
        </div>
      </template>
      <template #cell(createdAt)="{ item }">
        <span class="text-nowrap">{{ dateStr(item.createdAt) }}</span>
      </template>
      <template #cell(id)="{ item }">
        <code>{{ item.id }}</code>
        <div class="small text-muted" v-if="item.orderId">{{ item.orderId }}</div>
      </template>
      <template #cell(customer)="{ item }">
        <div v-if="item.customerName">
          {{ item.customerName }}
          <router-link v-if="item.localOrderId" :to="`/admin/order/${item.localOrderId}`" @click.native.stop>
            #{{ item.localOrderNum }}
          </router-link>
        </div>
        <div class="small text-muted">{{ item.contact }} {{ item.email }}</div>
      </template>
      <template #cell(amount)="{ item }">
        <span class="text-nowrap"><rupee>{{ money(item.amount) }}</rupee></span>
        <div class="small text-info text-nowrap" v-if="item.amountRefunded > 0">
          <rupee>{{ money(item.amountRefunded) }}</rupee> refunded
        </div>
      </template>
      <template #cell(method)="{ item }">
        <span class="text-uppercase">{{ item.method }}</span>
        <div class="small text-muted">{{ item.methodDetail }}</div>
      </template>
      <template #cell(status)="{ item }">
        <b-badge :variant="statusVariant(item)">{{ statusLabel(item) }}</b-badge>
        <div class="small text-danger" v-if="item.status === 'failed' && item.errorDescription">
          {{ item.errorDescription }}
        </div>
      </template>
    </b-table>

    <b-modal :visible="!!selected" @hidden="selected = null" :title="selected && selected.id" hide-footer size="lg">
      <div v-if="selected">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h3 class="mb-0"><rupee>{{ money(selected.amount) }}</rupee></h3>
          <b-badge :variant="statusVariant(selected)" class="p-2">{{ statusLabel(selected) }}</b-badge>
        </div>
        <b-alert variant="danger" show v-if="selected.status === 'failed'">
          <strong>{{ selected.errorCode }}</strong> &mdash; {{ selected.errorDescription }}
          <div class="small mt-1">
            <span v-if="selected.errorSource">Source: {{ selected.errorSource }}</span>
            <span v-if="selected.errorStep" class="ml-3">Step: {{ selected.errorStep }}</span>
            <span v-if="selected.errorReason" class="ml-3">Reason: {{ selected.errorReason }}</span>
          </div>
        </b-alert>
        <table class="table table-sm">
          <tbody>
          <tr v-for="r of detailRows(selected)" :key="r[0]">
            <th class="text-muted font-weight-normal w-25">{{ r[0] }}</th>
            <td class="text-break">{{ r[1] }}</td>
          </tr>
          <tr v-if="selected.localOrderId">
            <th class="text-muted font-weight-normal">Order</th>
            <td>
              <router-link :to="`/admin/order/${selected.localOrderId}`">#{{ selected.localOrderNum }}</router-link>
              ({{ selected.customerName }}, {{ selected.customerMobile }})
            </td>
          </tr>
          <tr v-if="selected.notes">
            <th class="text-muted font-weight-normal">Notes</th>
            <td><pre class="mb-0 small">{{ JSON.stringify(selected.notes, null, 2) }}</pre></td>
          </tr>
          </tbody>
        </table>
      </div>
    </b-modal>
  </section>
</template>
<script>
import axios from 'axios'
import {format, subDays} from 'date-fns'
import Rupee from '@/components/rupee'

const ymd = d => format(d, 'yyyy-MM-dd')

export default {
  name: 'AdminRazorpay',
  components: {Rupee},
  data: () => ({
    payments: [],
    loading: false,
    error: null,
    range: 'TODAY',
    from: ymd(new Date()),
    to: ymd(new Date()),
    filter: '',
    status: 'all',
    selected: null,
    statuses: [
      {key: 'all', label: 'All'},
      {key: 'captured', label: 'Captured'},
      {key: 'failed', label: 'Failed'},
      {key: 'authorized', label: 'Authorized'},
      {key: 'refunded', label: 'Refunded'},
      {key: 'created', label: 'Created'},
    ],
    fields: [
      {key: 'createdAt', label: 'Created'},
      {key: 'id', label: 'Payment'},
      {key: 'customer', label: 'Customer'},
      {key: 'amount', label: 'Amount', class: 'text-right'},
      {key: 'method', label: 'Method'},
      {key: 'status', label: 'Status'},
    ]
  }),
  created() {
    this.load()
  },
  computed: {
    isPaymentId() {
      return /^pay_[A-Za-z0-9]+$/.test(this.filter.trim())
    },
    counts() {
      return this.payments.reduce((acc, p) => {
        acc[p.status] = (acc[p.status] || 0) + 1
        return acc
      }, {})
    },
    capturedAmt() {
      return this.payments.filter(p => p.captured).reduce((s, p) => s + p.amount, 0)
    },
    refundedAmt() {
      return this.payments.reduce((s, p) => s + p.amountRefunded, 0)
    },
    successRate() {
      const done = this.payments.filter(p => p.status !== 'created').length
      if (!done) return '–'
      const ok = this.payments.filter(p => p.captured).length
      return `${Math.round(ok * 100 / done)}%`
    },
    filtered() {
      const f = this.filter.trim().toLowerCase()
      return this.payments
          .filter(p => this.status === 'all' || p.status === this.status)
          .filter(p => !f || [p.id, p.orderId, p.email, p.contact, p.customerName, p.customerMobile,
            p.localOrderNum && `#${p.localOrderNum}`, p.localOrderNum, p.description, p.methodDetail]
              .some(v => v != null && String(v).toLowerCase().includes(f)))
    }
  },
  methods: {
    onRangeChange() {
      const today = new Date()
      switch (this.range) {
        case 'TODAY':
          this.from = this.to = ymd(today)
          break
        case 'YESTERDAY':
          this.from = this.to = ymd(subDays(today, 1))
          break
        case 'SEVEN':
          this.from = ymd(subDays(today, 6))
          this.to = ymd(today)
          break
        case 'THIRTY':
          this.from = ymd(subDays(today, 29))
          this.to = ymd(today)
          break
      }
      this.load()
    },
    async load() {
      await this.fetch({from: this.from, to: this.to})
    },
    async searchById() {
      if (!this.isPaymentId || this.filtered.length) return
      await this.fetch({id: this.filter.trim()})
      this.status = 'all'
    },
    async fetch(params) {
      this.loading = true
      this.error = null
      try {
        const res = await axios.get('/api/admin/razorpay/payments', {params})
        this.payments = res.data
      } catch (e) {
        this.error = (e.response && e.response.data && (e.response.data.title || e.response.data)) || e.message
        this.payments = []
      } finally {
        this.loading = false
      }
    },
    money(v) {
      return Number(v || 0).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})
    },
    dateStr(epoch) {
      return format(new Date(epoch * 1000), 'dd MMM yyyy, hh:mm a')
    },
    statusLabel(p) {
      if (p.refundStatus === 'partial') return 'partially refunded'
      return p.status
    },
    statusVariant(p) {
      return {
        captured: 'success',
        failed: 'danger',
        authorized: 'warning',
        refunded: 'info',
        created: 'secondary'
      }[p.status] || 'secondary'
    },
    detailRows(p) {
      return [
        ['Created', this.dateStr(p.createdAt)],
        ['Razorpay Order', p.orderId],
        ['Method', [p.method && p.method.toUpperCase(), p.methodDetail].filter(Boolean).join(' · ')],
        ['Email', p.email],
        ['Contact', p.contact],
        ['Description', p.description],
        ['Refunded', p.amountRefunded > 0 ? `₹${this.money(p.amountRefunded)} (${p.refundStatus})` : null],
        ['Fee (incl. GST)', p.fee ? `₹${this.money(p.fee)} (GST ₹${this.money(p.tax)})` : null],
      ].filter(r => r[1])
    }
  }
}
</script>
<style>
.cursor-pointer {
  cursor: pointer;
}
</style>
